import React, { useContext, useEffect, useRef } from 'react';
import { UserDataContext } from 'lane-shared/contexts';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingStates from './components/general/LoadingStates';
import { Redirect, useLocation } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import { useFlag } from 'lane-shared/hooks';
import { useAuth0ConsolidateAuthTokenStorage } from 'lane-shared/hooks/auth0/useAuth0ConsolidateAuthTokenStorage';
import { FeatureFlag } from 'constants-flags';
import { useAuthClearSession } from './components/onboarding/Onboarding/useAuthClearSession';

function useClearSessionAndRedirectToIdentifier(
  hasAuth0SessionWithoutAppUser: boolean,
  hasAttemptedLogin: boolean,
  shouldClearSession: boolean,
  location: ReturnType<typeof useLocation>
) {
  const identifierUrl =
    window.location.origin +
    routes.identifier +
    (location.search || '');
  const clearSession = useAuthClearSession({
    returnTo: identifierUrl,
    emitLogout: false,
  });
  const hasStartedClear = useRef(false);

  useEffect(() => {
    if (
      !hasAuth0SessionWithoutAppUser ||
      !hasAttemptedLogin ||
      !shouldClearSession ||
      hasStartedClear.current
    ) {
      return;
    }

    hasStartedClear.current = true;

    clearSession().catch(() => {
      // Redirect still happens via auth0Logout; ignore cleanup errors
    });
  }, [
    hasAuth0SessionWithoutAppUser,
    hasAttemptedLogin,
    shouldClearSession,
    clearSession,
  ]);
}

export const RequireUserOnboarded = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  const {
    user,
    isLoggingIn: isLoggingInUser,
    hasAttemptedLogin,
    sessionInvalidatedByMeError,
    meQuerySucceeded,
  } = useContext(UserDataContext);

  const {
    isAuthenticated: isAuthenticatedViaAuth0,
    isLoading: isLoadingAuth0Authentication,
  } = useAuth0();

  const location = useLocation();

  const redirectExpiredSessionToSignIn = Boolean(
    useFlag(FeatureFlag.EnableOAuthAuth0UserDetection, false)
  );

  const isConsolidateAuthTokenStorageOn =
    useAuth0ConsolidateAuthTokenStorage();

  // TS-38584: under consolidate-on, require a positive `me` response
  // (with or without a user) before treating an authenticated Auth0
  // user as a new SSO signup, so transient `me` failures don't push
  // existing users to /signup.
  const needsToSignup = isConsolidateAuthTokenStorageOn
    ? isAuthenticatedViaAuth0 && meQuerySucceeded && !user
    : isAuthenticatedViaAuth0 && !user;

  const needsToOnboard = isAuthenticatedViaAuth0 && !user?.roles?.length;

  // TS-38584: under consolidate-on, any sessionInvalidatedByMeError
  // clears the session. Legacy keeps the EnableOAuthAuth0UserDetection gate.
  const isClearSessionPathEnabled =
    isConsolidateAuthTokenStorageOn || redirectExpiredSessionToSignIn;
  const shouldClearSessionOnMeError =
    isClearSessionPathEnabled && sessionInvalidatedByMeError;

  const hasAuth0SessionWithoutAppUser = isAuthenticatedViaAuth0 && !user;

  useClearSessionAndRedirectToIdentifier(
    hasAuth0SessionWithoutAppUser,
    hasAttemptedLogin,
    shouldClearSessionOnMeError,
    location
  );

  if (isLoadingAuth0Authentication || isLoggingInUser) {
    return <LoadingStates />;
  }

  if (!hasAttemptedLogin) {
    return <LoadingStates />;
  }

  const queryParams = location.search.substring(1);

  // TS-38584: hold the loader while waiting for the `me` verdict.
  if (
    isConsolidateAuthTokenStorageOn &&
    hasAuth0SessionWithoutAppUser &&
    !meQuerySucceeded &&
    !shouldClearSessionOnMeError
  ) {
    return <LoadingStates />;
  }

  if (needsToSignup) {
    if (shouldClearSessionOnMeError) {
      return <LoadingStates />;
    }

    return (
      <Redirect
        to={{
          pathname: `${routes.signup}/profile`,
          search: queryParams,
        }}
      />
    );
  }

  // TS-38584: hold the loader while the clear-session effect finishes.
  if (
    isConsolidateAuthTokenStorageOn &&
    hasAuth0SessionWithoutAppUser &&
    shouldClearSessionOnMeError
  ) {
    return <LoadingStates />;
  }

  if (needsToOnboard) {
    return (
      <Redirect
        to={{
          pathname: `${routes.onboarding}/profile`,
          search: queryParams,
        }}
      />
    );
  }

  return children;
};
